import React from 'react';
import { CookiesProvider } from 'react-cookie';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { BookOpenIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import {
  OmniFilter,
  Tooltip,
  useAccessControl,
} from '@plerionhq/design-system';
import { useIntegrationGroupFilter } from 'api/use-integration-group-filter';
import { isGlobalFilterRoute } from 'api/global-filter-routes';
import { useUserProfile } from '../../api/profile';
import { withErrorBoundary } from '../../hoc/withErrorBoundary';
import { ProfileDropDown } from '../ProfileDropDown/ProfileDropDown';
import { TenantSwitcher } from './TenantSwitcher';
import { Organization } from '../../api/organization';
import { DOCS_BASE_URL } from '../../constants/urls';
import { PleriTriggerButton } from '../../../../search/src/components/Pleri/components/PleriRenderWindow';

const NotificationBell = withErrorBoundary(
  React.lazy(() => import('../../micro-frontends/components/NotificationBell')),
);

export const NavBar = ({ orgData }: { orgData?: Organization }) => {
  const { data } = useUserProfile();
  const location = useLocation();
  const { t } = useTranslation('common');
  const { t: tShell } = useTranslation('containerInterface');

  const isSupportedRoute = isGlobalFilterRoute(
    location.pathname,
    location.search,
  );

  // Home offers integration groups too: its widgets read the group selection
  // and scope to the group's members.
  const { groups, isVisible: isIntegrationGroupFilterVisible } =
    useIntegrationGroupFilter({ isEnabled: isSupportedRoute });
  const integrationGroups = isIntegrationGroupFilterVisible ? groups : [];

  // Entitlements, not the record's roles: a SCIM-managed user's record lists
  // no roles, and their organization role arrives through a group. Both
  // organization built-ins read the organization plane, so this is the same
  // test the record's role level used to answer.
  const { can } = useAccessControl();
  const hasOrganizationRole = can({ action: 'Organization:Read' });
  const isHelpLinkEnabled =
    orgData?.profile?.type === 'direct' || hasOrganizationRole;

  return (
    <CookiesProvider>
      <nav className="relative top-0 z-[100] flex max-h-[48px] w-full max-w-full items-center border-b border-gray-200 bg-white px-2 py-1 shadow-sm dark:border-gray-700 dark:bg-gray-900">
        <div className="flex gap-3">
          <NavLink
            className="hover:bg-br-primary/5 flex flex-shrink-0 justify-self-start overflow-hidden rounded-md"
            to="/"
          >
            <img
              aria-hidden="true"
              alt={tShell('logo')}
              className="block h-[38px] flex-shrink-0 lg:!hidden"
              src={`${process.env.PUBLIC_PATH}logo-small.svg`}
            />
            <img
              alt={tShell('logo')}
              aria-hidden="true"
              className="hidden h-[38px] flex-shrink-0 px-[1px] py-[1px] lg:!inline"
              src={`${process.env.PUBLIC_PATH}logo-large.svg`}
            />
          </NavLink>
          <TenantSwitcher />
          {isSupportedRoute ? (
            <OmniFilter integrationGroups={integrationGroups} />
          ) : null}
        </div>
        <div className="ml-auto" />
        <PleriTriggerButton />
        <Tooltip label={t('resources')}>
          <a
            className="hover:bg-br-primary/10 flex flex-col items-center overflow-hidden rounded-md p-2 text-gray-600 dark:text-gray-400"
            data-testid="navbar-docs-link"
            href={DOCS_BASE_URL}
            rel="noreferrer"
            target="_blank"
          >
            <BookOpenIcon className="h-6 w-6" />
          </a>
        </Tooltip>

        {isHelpLinkEnabled && (
          <Tooltip label={t('support')}>
            <a
              className="hover:bg-br-primary/10 flex flex-col items-center overflow-hidden rounded-md p-2 text-gray-600 dark:text-gray-400"
              data-testid="navbar-help-link"
              href="https://external.support.plerion.com/servicedesk/customer/portal/3"
              rel="noopener noreferrer"
              target="_blank"
            >
              <SupportIcon />
            </a>
          </Tooltip>
        )}

        <div className="hover:bg-br-primary/10 flex items-center rounded-md text-gray-600 dark:text-gray-400">
          <NotificationBell />
        </div>

        {hasOrganizationRole ? (
          <Tooltip label={t('organizationAdmin')}>
            <Link
              className="bg-br-primary/10 focus:ring-br-primary hover:bg-br-primary/30 z-0 mx-2 my-1 flex items-center gap-2 rounded-md px-2 py-1 text-gray-900 focus:ring-2 focus:ring-offset-2 focus:outline-none dark:text-gray-100"
              data-testid="navbar-admin-link"
              to="/admin/tenants"
            >
              {t('admin')}
            </Link>
          </Tooltip>
        ) : null}

        <ProfileDropDown
          initials={
            data?.name
              ?.split(/\s/)
              .map((x) => x?.[0] || '')
              .join('')
              .slice(0, 2) || ''
          }
        />
      </nav>
    </CookiesProvider>
  );
};

const SupportIcon = () => (
  <svg className="h-6 w-6" viewBox="0 0 128 128">
    <path
      d="m110.307 60.153-7.807-1.393v-12.527a38.5 38.5 0 0 0 -77 0v12.528l-7.761 1.384a6.488 6.488 0 0 0 -5.56 6.455v14.48a6.476 6.476 0 0 0 5.514 6.448l12.873 2.3a8.753 8.753 0 0 0 6.648 6.2v7.993a9.631 9.631 0 0 0 9.621 9.62h4.842a8.39 8.39 0 0 0 8.192 6.629h8.262a8.379 8.379 0 1 0 0-16.758h-8.262a8.391 8.391 0 0 0 -8.192 6.629h-4.842a6.127 6.127 0 0 1 -6.121-6.12v-7.999a8.744 8.744 0 0 0 6.98-8.554v-27.255a8.736 8.736 0 0 0 -8.6-8.724v-5.256a24.91 24.91 0 0 1 49.82 0v5.256a8.736 8.736 0 0 0 -8.6 8.724v27.255a8.725 8.725 0 0 0 17.128 2.356l12.825-2.287a6.489 6.489 0 0 0 5.562-6.456v-14.481a6.474 6.474 0 0 0 -5.522-6.447zm-50.438 46.853h8.262a4.879 4.879 0 1 1 0 9.758h-8.262a4.879 4.879 0 0 1 0-9.758zm-44.19-25.925v-14.481a3.025 3.025 0 0 1 2.628-3l11.927-2.126v24.735l-11.975-2.133a3.011 3.011 0 0 1 -2.58-2.995zm28.515-20.868v27.255a5.23 5.23 0 1 1 -10.46 0v-27.255a5.23 5.23 0 0 1 10.46 0zm19.806-42.389a28.441 28.441 0 0 0 -28.41 28.409v5.929a8.765 8.765 0 0 0 -5.024 5.7l-1.566.275v-11.9a35 35 0 0 1 70 0v11.9l-1.564-.279a8.765 8.765 0 0 0 -5.024-5.7v-5.925a28.442 28.442 0 0 0 -28.412-28.409zm25.036 74.876a5.237 5.237 0 0 1 -5.23-5.231v-27.256a5.23 5.23 0 0 1 10.46 0v27.255a5.237 5.237 0 0 1 -5.23 5.232zm23.285-11.618a3.024 3.024 0 0 1 -2.628 3l-11.927 2.127v-24.737l11.975 2.134a3.01 3.01 0 0 1 2.58 2.995z"
      stroke="currentColor"
      strokeWidth={3}
    />
  </svg>
);
